export type SortDir = 'asc' | 'desc';

/** What the paginator emits: `page` is 1-based. */
export interface TablePage {
  page: number;
  pageSize: number;
}

/** What a lazy table emits when a header is clicked. */
export interface TableSort {
  field: string;
  dir: SortDir;
}

/** Reads `row.a.b` for field `'a.b'`; undefined if any step is missing. */
export function fieldValue(row: unknown, field: string): unknown {
  let value: unknown = row;
  for (const key of field.split('.')) {
    if (value === null || value === undefined) return undefined;
    value = (value as Record<string, unknown>)[key];
  }
  return value;
}

function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }
  const x = a instanceof Date ? a.getTime() : a;
  const y = b instanceof Date ? b.getTime() : b;
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  if (typeof x === 'boolean' && typeof y === 'boolean') return Number(x) - Number(y);
  return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Stable sort on a (possibly dotted) field. Empty values (null, undefined, '')
 * always go last, whichever the direction. Returns a new array.
 */
export function sortRows<T>(rows: readonly T[], field: string | undefined, dir: SortDir): T[] {
  if (!field) return [...rows];
  const sign = dir === 'asc' ? 1 : -1;
  const isEmpty = (v: unknown) => v === null || v === undefined || v === '';
  return rows
    .map((row, index) => ({ row, index, value: fieldValue(row, field) }))
    .sort((a, b) => {
      const ea = isEmpty(a.value);
      const eb = isEmpty(b.value);
      if (ea || eb) return ea === eb ? a.index - b.index : ea ? 1 : -1;
      return sign * compareValues(a.value, b.value) || a.index - b.index;
    })
    .map((entry) => entry.row);
}

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
}

export function clampPage(page: number, total: number, pageSize: number): number {
  return Math.min(Math.max(1, Math.floor(page) || 1), pageCount(total, pageSize));
}

/**
 * A server page that came back empty although rows remain: the queue shrank
 * under it (rows approved, completed or filtered away). The last page that
 * still has rows, to load instead; undefined when the page is fine.
 */
export function pastLastPage(page: number, rowsOnPage: number, total: number, pageSize: number): number | undefined {
  if (rowsOnPage > 0 || total <= 0 || page <= 1) return undefined;
  const last = pageCount(total, pageSize);
  return last < page ? last : undefined;
}

/** Rows shown on a 1-based page. */
export function pageSlice<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

/** Up to `size` page numbers centred on the current page (PrimeNG's page links). */
export function pageWindow(page: number, pages: number, size = 5): number[] {
  const count = Math.min(size, pages);
  const start = Math.min(Math.max(1, page - Math.floor(count / 2)), pages - count + 1);
  return Array.from({ length: count }, (_, i) => start + i);
}

/** "Showing 1 to 50 of 120 users" (0 to 0 when there are no rows). */
export function pageReport(page: number, pageSize: number, total: number, label: string): string {
  const first = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const last = Math.min(page * pageSize, total);
  return `Showing ${first} to ${last} of ${total} ${label}`.trim();
}
